// Core packages
import {
  HttpClient, HttpErrorResponse, HttpHeaders
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { EMPTY, Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { ConfigService } from "./config.service";
import { EnvironmentService } from "./environment.service";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private endpoint: string;
  private httpOptions: any;

  /**
   * Class constructor
   */
  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private router: Router,
    private cookieService: CookieService,
    private configService: ConfigService
  ) {
    this.endpoint = this.environmentService.getApiUrl();
    this.httpOptions = {};
  }

  /**
   * Get current values of session stored in cookie
   *
   * @since 1.0.0
   *
   * @returns object
   */
  getCookiesItems(): any {
    const sid = this.cookieService.get("s.id");
    const sexpiresAt = this.cookieService.get("s.expiresAt");
    return { sid, sexpiresAt };
  }

  /**
   * This method is a wrapper to call back-end APIs
   *
   * @since 1.0.0
   *
   * @param method HTTP Verbs to use. Can be 'GET', 'POST', 'PUT', 'DELETE'
   * @param path The endpoint relative path
   * @param [params] Optional api call params that needs to be attached to the call
   * @param extraOptions optiona extra option attached to the call
   * @param autoRefresh
   * @param supplier Route API to new Suppliers BE
   * @param version API version override (e.g. 'v2'). Defaults to environment.apiVersion
   * @returns Observable<any>
   */
  apiCall(
    method: string,
    path: string,
    params?: any,
    extraOptions?: any,
    autoRefresh?: boolean,
    supplier?: boolean,
    version?: string
  ): Observable<any> {
    if (typeof supplier !== "undefined" && supplier) {
      this.endpoint = this.environmentService.getSuppliersApiUrl();
    } else {
      this.endpoint = this.environmentService.getApiUrl(version);
    }

    const autoRefreshToken =
      typeof autoRefresh === "undefined" ? true : autoRefresh;

    if (typeof method === "undefined") {
      return of("Missing method");
    }

    const functionName = "apiCall" + method.toUpperCase();
    const functionToCall = this[functionName];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const thePath = path.endsWith("/") ? path.substring(-1) : path;

    // Get session data
    // If user is already logged in, just add his token
    // to the request header
    const sessData = this.getCookiesItems();
    const sid = sessData.sid;
    const sexpiresAt = sessData.sexpiresAt;

    // Init headers
    const acceptLang =
      this.cookieService.get("lang") || this.configService.defaultLang;
    let headers = new HttpHeaders().set("Accept-Language", acceptLang);
    if (extraOptions && extraOptions.contentType) {
      if (extraOptions.contentType !== "auto") {
        headers = headers.append("Content-Type", extraOptions.contentType);
      } else {
        // headers = headers.set('Content-Type', '');
        headers = headers.delete("Content-Type");
      }
      delete extraOptions.contentType; // Remove extra option after this line
    } else {
      headers = headers.append("Content-Type", "application/json");
    }
    if (sid && moment.now() < parseInt(sexpiresAt, 10)) {
      headers = headers.append("Authorization", `Bearer ${sid}`);
    }
    // Opt-in: unsubscribing aborts the request. Off by default, since callers that unsubscribe on
    // destroy rely on their requests reaching the back-end anyway (a save before leaving a page). Not
    // carried by the retry after a 401 refresh: callers that need it pass autoRefresh = false.
    const abortOnUnsubscribe = !!(extraOptions && extraOptions.abortOnUnsubscribe === true);
    if (extraOptions && extraOptions.hasOwnProperty("abortOnUnsubscribe")) {
      delete extraOptions.abortOnUnsubscribe;
    }
    if (extraOptions && extraOptions["X-Refresh-Token"]) {
      headers = headers.append(
        "X-Refresh-Token",
        `Bearer ${extraOptions["X-Refresh-Token"]}`
      );
    }

    const requestedRole = this.cookieService.get("requestedRole");
    if (requestedRole) {
      headers = headers.append("X-Requested-Role", requestedRole);
    }
    const requestedCompany = this.cookieService.get("requestedCompany");
    if (requestedCompany) {
      headers = headers.append("X-Requested-Company", requestedCompany);
    }

    // Set httpOptions
    this.httpOptions = {
      headers,
    };

    if (typeof functionToCall === "function") {
      return new Observable<any>((obs) => {
        const theParams = params || {};

        // Remove property with empty, undefined or null value
        // from params since they are not useful and - also -
        // since back-end could not accept undefined|null values
        // @see https://stackoverflow.com/questions/25421233/javascript-removing-undefined-fields-from-an-object
        // Object.keys(theParams).forEach(key => !theParams[key] && delete theParams[key]);
        Object.keys(theParams).forEach(
          (key) =>
            (theParams[key] === "undefined" ||
              theParams[key] === undefined ||
              theParams[key] === null) &&
            delete theParams[key]
        );

        // Remove 'requiredFields' from params since it's front-end-only data
        if (theParams.hasOwnProperty("requiredFields")) {
          delete theParams.requiredFields;
        }

        const obs1 = self[functionName](thePath, theParams, extraOptions);
        const requestSubscription = obs1.subscribe(
          (res) => {
            return obs.next(res);
          },
          (err) => {
            const refreshTokenExp = +this.cookieService.get(
              "s.refreshTokenExpiresAt"
            );
            // If error status is 401 means that we are not logged in
            // But before logging out, let's check if a not-expired refresh-token
            // is saved in our cookies.
            // If so, let's try to re-authenticate with that refreshToken
            if (
              autoRefreshToken &&
              err.status === 401 &&
              this.cookieService.get("s.refreshToken") &&
              !(moment.unix(moment.now()) > moment.unix(refreshTokenExp))
            ) {
              const refreshToken = this.cookieService.get("s.refreshToken");
              let headers2 = new HttpHeaders().set(
                "Accept-Language",
                this.cookieService.get("lang")
              );
              headers2 = headers2.append("Content-Type", "application/json");
              headers2 = headers2.append(
                "X-Refresh-Token",
                `Bearer ${refreshToken}`
              );

              return this.http
                .post<any>(
                  this.environmentService.getApiUrl("v2") +
                    "/auth/refreshtoken",
                  {},
                  { headers: headers2 }
                )
                .subscribe(
                  (result) => {
                    this.setSession(result.data || result);
                    return this.apiCall(
                      method,
                      path,
                      params,
                      extraOptions,
                      false,
                      supplier,
                      version
                    ).subscribe(
                      (res2) => {
                        obs.next(res2);
                      },
                      (err2) => {
                        obs.error(err2);
                      },
                      () => {
                        return obs.complete();
                      }
                    );
                  },
                  () => {
                    obs.error(err);
                  }
                );

              // return obs.error(err);
            } else {
              return obs.error(err);
            }
          },
          () => {
            return obs.complete();
          }
        );

        // Teardown: only an opt-in call aborts its request when unsubscribed (@see abortOnUnsubscribe)
        return () => {
          if (abortOnUnsubscribe) {
            requestSubscription.unsubscribe();
          }
        };
      });
    } else {
      console.warn("return EMPTY");
      return EMPTY;
    }
  }

  /**
   * Perform a POST API call with given path and
   * given params
   *
   * @since 1.0.0
   */
  private apiCallPOST(
    path: string,
    params?: object,
    extraOptions?: object
  ): Observable<any> {
    const options = this.httpOptions;
    if (typeof extraOptions !== "undefined") {
      Object.assign(options, extraOptions);
    }
    return this.http
      .post<any>(this.endpoint + "/" + path, params, options) // JSON.stringify(params)
      .pipe(catchError(this.handleError));
  }

  /**
   * Perform a PUT API call with given path and
   * given params
   *
   * @since 1.0.0
   */
  private apiCallPUT(
    path: string,
    params?: object,
    extraOptions?: object
  ): Observable<any> {
    const options = this.httpOptions;
    if (typeof extraOptions !== "undefined") {
      Object.assign(options, extraOptions);
    }
    return this.http
      .put(this.endpoint + "/" + path, params, options)
      .pipe(catchError(this.handleError));
  }

  /**
   * Perform a PATCH API call with given path and
   * given params
   *
   * @since 1.0.0
   */
  private apiCallPATCH(
    path: string,
    params?: object,
    extraOptions?: object
  ): Observable<any> {
    const options = this.httpOptions;
    if (typeof extraOptions !== "undefined") {
      Object.assign(options, extraOptions);
    }
    return this.http
      .patch(this.endpoint + "/" + path, params, options)
      .pipe(catchError(this.handleError));
  }

  /**
   * Perform a GET API call with given path and
   * given params
   *
   * @since 1.0.0
   */
  private apiCallGET(
    path: string,
    params?: any,
    extraOptions?: object
  ): Observable<any> {
    const options = this.httpOptions;
    if (typeof params !== "undefined") {
      options.params = params;
    }
    if (typeof extraOptions !== "undefined") {
      Object.assign(options, extraOptions);
    }
    return this.http
      .get(this.endpoint + "/" + path, options)
      .pipe(catchError(this.handleError));
  }

  /**
   * Perform a DELETE API call with given path and
   * given params
   *
   * @since 1.0.0
   */
  private apiCallDELETE(
    path: string,
    params?: object,
    extraOptions?: object
  ): Observable<any> {
    const options = this.httpOptions;
    if (typeof params !== "undefined") {
      options.params = params;
    }
    if (typeof extraOptions !== "undefined") {
      Object.assign(options, extraOptions);
    }
    return this.http
      .delete<any>(this.endpoint + "/" + path, options)
      .pipe(catchError(this.handleError));
  }

  /**
   * Since we are not using a type checker,
   * the response should be extracted.
   *
   * @since 1.0.0
   */
  private extractData(res: Response) {
    const body = res;
    return body || {};
  }

  /**
   * Handle http errors
   *
   * @since 1.0.0
   */
  private handleError(error: HttpErrorResponse): Observable<any> {
    // const message = error.error.message;
    // if (typeof context.toastrService !== 'undefined') {
    //   context.toastrService.error(message);
    // }
    if (error.status === 0) {
      return of(
        new Error("Unexpected error. Please reload the page and try again")
      );
    }
    return throwError(error);
  }

  /**
   * Set given login result data as current session
   *
   * @since 1.0.0
   *
   * @param data Session data coming from back-end
   */
  private setSession(data: any): void {
    const expiresAt = moment().add(data.expiresIn, "second");
    this.cookieService.set(
      "s.id",
      data.token,
      null,
      "/",
      this.environmentService.getFrontEndHost(),
      true
    );
    this.cookieService.set(
      "s.expiresAt",
      JSON.stringify(expiresAt.valueOf()),
      null,
      "/",
      this.environmentService.getFrontEndHost(),
      true
    );
    if (data.user && data.user.userRoles && data.user.userRoles[0]) {
      this.cookieService.set(
        "requestedRole",
        data.user.userRoles[0].role._id, // data.user.roles[0]._id,
        null,
        "/",
        this.environmentService.getFrontEndHost(),
        true
      );
      if (data.user.userRoles[0].company) {
        this.cookieService.set(
          "requestedCompany",
          data.user.userRoles[0].company._id.toString(), // data.user.roles[0]._id,
          null,
          "/",
          this.environmentService.getFrontEndHost(),
          true
        );
      }
    }
    if (data.refreshToken) {
      this.cookieService.set(
        "s.refreshToken",
        data.refreshToken,
        null,
        "/",
        this.environmentService.getFrontEndHost(),
        true
      );
    }
    if (data.refreshExpiresIn) {
      const refreshExpiresAt = moment().add(data.refreshExpiresIn, "second");
      this.cookieService.set(
        "s.refreshTokenExpiresAt",
        JSON.stringify(refreshExpiresAt.valueOf()),
        null,
        "/",
        this.environmentService.getFrontEndHost(),
        true
      );
    }
  }
}
